import { useContext, useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiMap,
  FiCheckCircle,
  FiXCircle,
  FiAlertOctagon,
  FiX,
  FiWifiOff,
  FiSearch,
} from "react-icons/fi";
import { useSnackbar } from "notistack";
import { api } from "../../services/api";

import { ItemPagination } from "../../components/ItemPagination";
import { AuthContext } from "../../contexts/AuthContext";
import { PageTitle } from "../../components/PageTitle";
import { EmptyState } from "../../components/EmptyState";

interface Costumer {
  id: string;
  name: string;
  status: string;
  address: string;
  number: string;
  district: string;
  city: string;
  state: string;
  zip: string;
  latitude: number | null;
  longitude: number | null;
  emergency: boolean;
  zone?: {
    id: string;
    name: string;
    color?: string;
  };
}

interface Zone {
  id: string;
  name: string;
  status: string;
}

interface CostumerStats {
  totalActive: number;
  totalInactive: number;
  totalWithoutCoordinates: number;
  totalEmergency: number;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  color: string;
}

function MetricCard({ title, value, icon: Icon, color }: MetricCardProps) {
  // Mapear cores de fundo para cores de gradiente, sombra e texto
  const colorMap: Record<string, { gradient: string; shadow: string; text: string; bgAccent: string }> = {
    "bg-green-500": {
      gradient: "from-green-500 to-emerald-500",
      shadow: "shadow-green-500/30",
      text: "from-green-600 to-emerald-600",
      bgAccent: "from-green-500/10 to-emerald-500/5"
    },
    "bg-red-500": {
      gradient: "from-red-500 to-rose-500",
      shadow: "shadow-red-500/30",
      text: "from-red-600 to-rose-600",
      bgAccent: "from-red-500/10 to-rose-500/5"
    },
    "bg-amber-500": {
      gradient: "from-amber-500 to-orange-500",
      shadow: "shadow-amber-500/30",
      text: "from-amber-600 to-orange-600",
      bgAccent: "from-amber-500/10 to-orange-500/5"
    },
  };

  const colorClasses = colorMap[color] || {
    gradient: "from-[#0085BD] to-[#006a96]",
    shadow: "shadow-[#0085BD]/30",
    text: "from-[#0085BD] to-[#006a96]",
    bgAccent: "from-[#0085BD]/10 to-[#006a96]/5"
  };

  return (
    <div className="group relative bg-white rounded-2xl p-3 sm:p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
      {/* Elemento decorativo no canto */}
      <div className={`absolute top-0 right-0 w-12 sm:w-20 h-12 sm:h-20 bg-gradient-to-br ${colorClasses.bgAccent} rounded-bl-full`} />

      <div className="relative flex items-center gap-2 sm:gap-4">
        {/* Ícone com gradiente - menor no mobile */}
        <div className={`w-9 h-9 sm:w-12 sm:h-12 shrink-0 rounded-lg sm:rounded-xl bg-gradient-to-br ${colorClasses.gradient} flex items-center justify-center shadow-md sm:shadow-lg ${colorClasses.shadow} group-hover:scale-110 transition-transform duration-300`}>
          <Icon size={18} className="text-white sm:hidden" />
          <Icon size={24} className="text-white hidden sm:block" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Valor com gradiente de texto */}
          <p className={`text-xl sm:text-2xl font-bold bg-gradient-to-r ${colorClasses.text} bg-clip-text text-transparent`}>
            {value}
          </p>
          <p className="text-xs sm:text-sm text-gray-500 font-medium truncate">{title}</p>
        </div>
      </div>
    </div>
  );
}

export function Costumers() {
  useContext(AuthContext);
  const [costumers, setCostumers] = useState<Costumer[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [stats, setStats] = useState<CostumerStats>({
    totalActive: 0,
    totalInactive: 0,
    totalWithoutCoordinates: 0,
    totalEmergency: 0,
  });
  const [selectedZone, setSelectedZone] = useState<string>("");
  const [withoutCoordinates, setWithoutCoordinates] = useState<string>("");
  const [emergencyFilter, setEmergencyFilter] = useState<string>("");
  const [nameFilter, setNameFilter] = useState<string>("");
  const [debouncedNameFilter, setDebouncedNameFilter] = useState<string>("");
  const [changeStatus, setChangeStatus] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  // Debounce para filtro de nome (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNameFilter(nameFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [nameFilter]);

  // Modal de observação emergencial
  const [emergencyModalOpen, setEmergencyModalOpen] = useState(false);
  const [emergencyModalCostumer, setEmergencyModalCostumer] =
    useState<Costumer | null>(null);
  const [emergencyObservation, setEmergencyObservation] = useState("");
  const [submittingObservation, setSubmittingObservation] = useState(false);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    // Carregar zonas e estatísticas
    api.get("/zones").then(response => {
      setZones(response.data);
    });
    api.get("/costumers/stats").then(response => {
      setStats(response.data);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (selectedZone === "null") {
      params.zone_id = "null";
    } else if (selectedZone) {
      params.zone_id = selectedZone;
    }
    if (withoutCoordinates === "true") {
      params.without_coordinates = "true";
    }
    if (emergencyFilter) {
      params.emergency = emergencyFilter;
    }
    if (debouncedNameFilter.trim()) {
      params.name = debouncedNameFilter.trim();
    }

    api
      .get("/costumers", { params })
      .then(response => {
        setCostumers(response.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        navigate("/signin");
      });
  }, [
    changeStatus,
    selectedZone,
    withoutCoordinates,
    emergencyFilter,
    debouncedNameFilter,
    navigate,
  ]);

  function formatAddress(costumer: Costumer): string | undefined {
    const parts: string[] = [];

    // Endereço e número
    if (costumer.address) {
      parts.push(
        costumer.number
          ? `${costumer.address}, ${costumer.number}`
          : costumer.address,
      );
    }

    // Bairro
    if (costumer.district) {
      parts.push(costumer.district);
    }

    // Cidade/Estado
    if (costumer.city && costumer.state) {
      parts.push(`${costumer.city}/${costumer.state}`);
    } else if (costumer.city) {
      parts.push(costumer.city);
    } else if (costumer.state) {
      parts.push(costumer.state);
    }

    // CEP
    if (costumer.zip) {
      parts.push(`CEP: ${costumer.zip}`);
    }

    return parts.length > 0 ? parts.join(" - ") : undefined;
  }

  async function handleChangeStatus(id: string, newStatus: string) {
    api.patch(`costumers/status/${id}/${newStatus === "A" ? "I" : "A"}`);
    setTimeout(() => {
      setChangeStatus(changeStatus + 1);
    }, 1000);
  }

  // Abre modal de observação emergencial
  function handleOpenEmergencyModal(costumer: Costumer) {
    setEmergencyModalCostumer(costumer);
    setEmergencyObservation("");
    setEmergencyModalOpen(true);
  }

  // Cria observação emergencial
  async function handleCreateEmergencyObservation() {
    if (!emergencyModalCostumer || !emergencyObservation.trim()) return;

    setSubmittingObservation(true);
    try {
      await api.post("/costumer-observations", {
        costumer_id: emergencyModalCostumer.id,
        text: emergencyObservation.trim(),
      });
      enqueueSnackbar("Observação emergencial criada com sucesso!", {
        variant: "success",
      });
      setEmergencyModalOpen(false);
      setEmergencyModalCostumer(null);
      setEmergencyObservation("");
      // Recarrega lista para atualizar status de emergência
      setChangeStatus(prev => prev + 1);
    } catch (error) {
      enqueueSnackbar("Erro ao criar observação emergencial", {
        variant: "error",
      });
    } finally {
      setSubmittingObservation(false);
    }
  }

  return (
    <div className="container mx-auto">
      <PageTitle title="Clientes" backButton backPath="/home" />

      <div className="flex items-start justify-between mb-6">
        <div className="flex gap-3">
          <Link
            to="/costumers/new"
            className="group bg-[#0085BD] text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg hover:bg-[#006a96] transition-all duration-200 hover:shadow-xl"
            title="Adicionar Cliente"
          >
            <FiPlus size={24} className="transition-transform duration-200 group-hover:rotate-90" />
          </Link>
          <Link
            to="/costumers/map"
            className="group bg-[#28A745] text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg hover:bg-[#218838] transition-all duration-200 hover:shadow-xl"
            title="Visualizar no Mapa"
          >
            <FiMap size={24} className="transition-transform duration-200 group-hover:scale-110" />
          </Link>
        </div>
      </div>

      {/* Big Numbers */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          title="Clientes Ativos"
          value={stats.totalActive}
          icon={FiCheckCircle}
          color="bg-green-500"
        />
        <MetricCard
          title="Clientes Inativos"
          value={stats.totalInactive}
          icon={FiXCircle}
          color="bg-red-500"
        />
        <MetricCard
          title="Emergências"
          value={stats.totalEmergency}
          icon={FiAlertOctagon}
          color="bg-red-500"
        />
        <MetricCard
          title="Sem Coordenadas"
          value={stats.totalWithoutCoordinates}
          icon={FiWifiOff}
          color="bg-amber-500"
        />
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6">
        <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4 rounded-t-2xl">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
            </svg>
            Filtros
          </h3>
        </div>
        <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <span className="text-sm font-semibold text-gray-700">
              Cliente
            </span>
            <div className="relative">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={nameFilter}
                onChange={e => setNameFilter(e.target.value)}
                placeholder="Digite o nome do cliente..."
                className="h-[52px] pl-11 pr-4 w-full rounded-lg text-gray-700 bg-white border border-gray-200 hover:border-[#0085BD] focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] transition-colors placeholder-gray-400"
              />
            </div>
          </div>
          <div className="space-y-2">
            <span className="text-sm font-semibold text-gray-700">
              Zona
            </span>
            <select
              value={selectedZone}
              onChange={e => setSelectedZone(e.target.value)}
              className="h-[52px] px-4 w-full rounded-lg text-gray-700 bg-white border border-gray-200 hover:border-[#0085BD] focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] transition-colors"
            >
              <option value="">Todas as zonas</option>
              <option value="null">Sem zona definida</option>
              {zones
                .filter(zone => zone.status === "A")
                .map(zone => (
                  <option key={zone.id} value={zone.id}>
                    {zone.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="space-y-2">
            <span className="text-sm font-semibold text-gray-700">
              Sem Coordenadas
            </span>
            <select
              value={withoutCoordinates}
              onChange={e => setWithoutCoordinates(e.target.value)}
              className="h-[52px] px-4 w-full rounded-lg text-gray-700 bg-white border border-gray-200 hover:border-[#0085BD] focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] transition-colors"
            >
              <option value="">Todos</option>
              <option value="true">Sim</option>
            </select>
          </div>
          <div className="space-y-2">
            <span className="text-sm font-semibold text-gray-700">
              Emergência
            </span>
            <select
              value={emergencyFilter}
              onChange={e => setEmergencyFilter(e.target.value)}
              className="h-[52px] px-4 w-full rounded-lg text-gray-700 bg-white border border-gray-200 hover:border-[#0085BD] focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] transition-colors"
            >
              <option value="">Todos</option>
              <option value="true">Sim</option>
              <option value="false">Não</option>
            </select>
          </div>
        </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0085BD]" />
        </div>
      )}
      {!loading && costumers.length > 0 && (
        <div className="space-y-2">
          {costumers.map((costumer: Costumer) => (
            <ItemPagination
              key={costumer.id}
              title={costumer.name}
              description={costumer.zone?.name || "Sem zona definida"}
              subtitle={formatAddress(costumer)}
              zoneColor={costumer.zone?.color}
              id={costumer.id}
              status={costumer.status}
              hasDelete
              hasEmergency
              isEmergency={costumer.emergency}
              onEmergencyClick={() => handleOpenEmergencyModal(costumer)}
              hasWarning={!costumer.latitude || !costumer.longitude}
              warningMessage="Cliente sem coordenadas (endereço incorreto)"
              handleChangeStatus={() =>
                handleChangeStatus(costumer.id, costumer.status)
              }
            />
          ))}
        </div>
      )}
      {!loading && costumers.length === 0 && <EmptyState type="costumers" />}

      {/* Modal de Observação Emergencial */}
      {emergencyModalOpen && emergencyModalCostumer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-red-500 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FiAlertOctagon className="text-white" size={24} />
                <h2 className="text-lg font-semibold text-white">
                  Observação Emergencial
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setEmergencyModalOpen(false)}
                className="text-white hover:text-red-200 transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Adicionar observação emergencial para{" "}
                <span className="font-semibold text-[#0085BD]">
                  {emergencyModalCostumer.name}
                </span>
              </p>

              <textarea
                value={emergencyObservation}
                onChange={e => setEmergencyObservation(e.target.value)}
                placeholder="Descreva a situação de emergência..."
                className="w-full h-32 px-4 py-3 rounded-lg border border-gray-200 focus:border-red-500 focus:ring-1 focus:ring-red-500 resize-none text-gray-700"
              />

              <p className="text-xs text-gray-400 mt-2">
                Ao criar esta observação, o cliente será marcado como
                emergencial.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEmergencyModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreateEmergencyObservation}
                disabled={!emergencyObservation.trim() || submittingObservation}
                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submittingObservation ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <FiAlertOctagon size={16} />
                    Criar Emergência
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
